import React, { useEffect, useState } from "react";
import ErrorComponent from "Components/ErrorComponent";
import Loading from "Components/Loading";
import ReportDetailsHeader from "Components/ReportDetailsHeader";
import * as Yup from "yup";
import {
    Get,
} from "VesselMaster/vesselMaster.hooks";
import { Formik } from "formik";
import apiGlobal from "global/api.global";
import { useQuery } from "react-query";
import {
    Row,
    Col,
    Card,
    CardHeader,
} from "reactstrap";
import { queryKeyes } from "shared/queryKeys";
import { queryClient } from "react-query/queryClient";
import { checkValidArray, handleServerResponse, isFormUnchanged, useSafeFormikContext } from "GenericForms/Helper";
import env from "environment_system/env_system";
import { useDocumentTitle } from "Components/useDocument.hooks";
import { AddInfoConstant } from "shared/constants";
import { commonValidationMessages, PeninsularGeneratedLoadError } from "Components/ValidationErrorMessages";
import RunningHoursStructure from "./RunningHoursStructure";
import { RootState } from "index";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentReportAction } from "Store/Generic/ReportingSlice";
import { useEditReport } from "VesselReportingInfo/EditCompletedReports/EditReportProvider";

interface RunningHoursComponentType {
    toggleDynamicTabs: any;
    setErrorMessage: any,
}

const RunningHoursComponent = ({
    toggleDynamicTabs,
    setErrorMessage,
}: RunningHoursComponentType) => {
    const { previousEnggTabIndex } = useEditReport();
    useDocumentTitle("Machinery Running Hours Report - ecoSAIL");
    /** State variables start */
    const { VesselID, VoyageID, ReportID, CurrentReport, reportingLoading, ConfigurationLimits, reportingError } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    const machineryRunningHoursCurrentReport = CurrentReport?.data?.chief_engineer?.machinery_running_hour ?? null;
    const [peninsularAddInfo, setPeninsularAddInfo] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const isPeninsularAddInfo = env?.additional_info_engg === AddInfoConstant.PENISULAR_ENGG ? true : false;
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    const RunningHrsMachineries = ConfigurationLimits?.vessel_machinery_list?.filter((item: any) => item.running_hour === true);
    /** State variables end */

    /** Machinery running hours object used for edit */
    const {
        data: PeninsularAddObject,
        isLoading: PeninsularAddObjectLoading,
    } = useQuery(
        [queryKeyes.peninsular.PeninsularLoadGeneratedObject.key, VesselID, ReportID],
        async () => {
            return await Get(queryKeyes.peninsular.PeninsularLoadGeneratedObject.url(VesselID, ReportID), VesselID, ReportID);
        },
        { staleTime: Infinity }
    );
    /** Queries end */

    /** Assign initial values to formik object 
     * If RunningHoursObject exists and is not empty, initialize it
     * If RunningHrsMachineries exists and has data
     * If MachineryRunningHoursMachinery exists and has data
     * If MachineryRunningHoursMachinery error
     */
    const getInitialValues = () => {
        let obj: any[] = [];
        if (checkValidArray(editInitialValues?.chief_engineer?.machinery_running_hour)) {
            obj = editInitialValues.chief_engineer.machinery_running_hour;
        } else if (checkValidArray(machineryRunningHoursCurrentReport)) {
            obj = [...machineryRunningHoursCurrentReport];
        } else if (checkValidArray(RunningHrsMachineries)) {
            RunningHrsMachineries.forEach((machine: any) => {
                // Find the existing machine in obj
                const existingMachine = obj.find(
                    (item: any) =>
                        item.vessel_machinery === machine.id &&
                        item.machinery_precedence_id === machine.machinery_precedence_id &&
                        item.vessel === machine.vessel
                );
                if (!existingMachine) {
                    // If machine is NOT present, add a new object
                    obj.push({
                        machinery_precedence_id: machine.machinery_precedence_id,
                        vessel_machinery: machine.id,
                        running_hours: null,
                        vessel_machinery_name: machine.vessel_machinery_name,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                        running_counter_value: null,
                    });
                }
            });
        }
        return obj;
    };

    /** Assign values to iniital object of Peninsular Additional Info */
    const getPeninsularAddInfo = () => {
        let arr: any[] = [];
        if (env?.additional_info_engg === AddInfoConstant.PENISULAR_ENGG) {
            if (checkValidArray(PeninsularAddObject) && checkValidArray(RunningHrsMachineries)) {
                arr.push(...PeninsularAddObject);
                RunningHrsMachineries?.forEach((machine: any) => {
                    if (!PeninsularAddObject.some((item: any) => item.vessel_machinery === machine.id)) {
                        arr.push({
                            load_generated: null,
                            vessel_machinery: machine.id,
                            vessel: VesselID,
                            voyage_information: VoyageID,
                            vessel_reporting_information: ReportID,
                        });
                    }
                });
                return arr;
            } else {
                if (checkValidArray(RunningHrsMachineries)) {
                    RunningHrsMachineries?.forEach((machine: any) => {
                        arr.push({
                            load_generated: null,
                            vessel_machinery: machine.id,
                            vessel: VesselID,
                            voyage_information: VoyageID,
                            vessel_reporting_information: ReportID,
                        });
                    });
                    return arr;
                }
            }
        } else {
            return [];
        }
    }

    /** useEffect */
    useEffect(() => {
        if (isPeninsularAddInfo) {
            setPeninsularAddInfo(true);
        } else {
            setPeninsularAddInfo(false);
        }
    }, [isPeninsularAddInfo])
    useEffect(() => {
        RunningHoursFormik.initialValues.runningHrs = getInitialValues();
        RunningHoursFormik.initialValues.peninsularAddInfo = getPeninsularAddInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [RunningHrsMachineries, machineryRunningHoursCurrentReport]);
    /** useEffect end */

    /** Running Hours Formik object */
    let RunningHoursFormik = {
        initialValues: {
            runningHrs: getInitialValues(),
            peninsularAddInfo: getPeninsularAddInfo()
        },
        validationSchema: env?.form_validation ? Yup.object().shape({
            runningHrs: Yup.array().of(
                Yup.object().shape({
                    running_hours: Yup.string()
                        .matches(/^(?:25(?:\.00?)?|(?:[0-1]?\d|2[0-4])(?:\.\d{1,2})?)$/, commonValidationMessages.max25)
                        .when("vessel_machinery", {
                            is: (value: any) => !(RunningHrsMachineries?.filter((machine: any) => machine.id === value)[0]?.running_hour), // Condition check
                            then: (schema) => schema.nullable(),
                            otherwise: (schema) => schema.required(commonValidationMessages.required),
                        }),
                    running_counter_value: Yup
                        .string().matches(/^\d{0,10}(\.\d{1,4})?$/, `${commonValidationMessages.before10after4}`).nullable(),
                })
            ),
            peninsularAddInfo: Yup.array().of(
                Yup.object().shape({
                    load_generated: Yup.string().matches(/^\d{0,3}(?:\.\d{1,2})?$/, commonValidationMessages.before3after2)
                        .test('', PeninsularGeneratedLoadError, value => {
                            if (isPeninsularAddInfo && parseFloat(value) > 860) {
                                return false;
                            }
                            return true;
                        })
                        .nullable(),
                }),
            )
        }) : null,
    };

    /** Running hours form submit */
    const RunningHoursSubmit = async (values: any | any[], actions: any) => {
        const responseArray: any = [];
        actions.setSubmitting(true);
        responseArray.push(apiGlobal.post(`/machinery_running_hours/`, values.runningHrs));
        if (isPeninsularAddInfo) {
            if (checkValidArray(PeninsularAddObject)) {
                values.peninsularAddInfo.forEach((val: any) => {
                    if (val?.id > 0) {
                        responseArray.push(apiGlobal.put(`${queryKeyes.peninsular.PeninsularLoadGenerated.url()}${val.id}/`, val));
                    } else {
                        responseArray.push(apiGlobal.post(queryKeyes.peninsular.PeninsularLoadGenerated.url(), val));
                    }
                })
            } else {
                responseArray.push(apiGlobal.post(queryKeyes.peninsular.PeninsularLoadGenerated.url(), values.peninsularAddInfo));
            }
        }
        await handleServerResponse(responseArray).then(async (res) => {
            if (res === true) {
                dispatch(getCurrentReportAction(VesselID, ReportID));
                if (isPeninsularAddInfo) {
                    queryClient.invalidateQueries(queryKeyes.peninsular.PeninsularLoadGeneratedObject.key);
                }
                if (env?.form_validation === true) {
                    setErrorMessage(null)
                    toggleDynamicTabs(previousEnggTabIndex + 1);
                }
            } else {
                setErrorMessage(res)
            }
            actions.setSubmitting(false);
        })
        actions.setSubmitting(false);
    }

    return (
        <Card className="p-0 mb-0 border-0">
            <CardHeader className="p-2">
                <div className="text-center">
                    <Row>
                        <Col>
                            <h4 className="page_title pos-start mb-0">
                                Machinery Running Hours
                            </h4>
                            <p className="card-title-desc pos-start">
                                All readings since last noon report
                            </p>
                        </Col>
                        <Col>
                            <ReportDetailsHeader />
                        </Col>
                    </Row>
                </div>
            </CardHeader>
            {(reportingLoading || PeninsularAddObjectLoading) && <Loading message="Loading required data!" />}
            {reportingError && <ErrorComponent message="Unable to load required data!" />}
            {!(reportingLoading || PeninsularAddObjectLoading) &&
                !reportingError && (
                    <Formik
                        onSubmit={async (values: any, actions: any) => {
                            if (env?.form_validation === false || isFormUnchanged(RunningHoursFormik.initialValues, values)) {
                                setErrorMessage(null)
                                toggleDynamicTabs(previousEnggTabIndex + 1);
                                return;
                            }
                            actions.setSubmitting(true);
                            await RunningHoursSubmit(values, actions);
                        }}
                        initialValues={RunningHoursFormik.initialValues}
                        validationSchema={env?.form_validation === true ? RunningHoursFormik.validationSchema : null}
                        key={refreshKey}
                    >
                        {() => (
                            <RunningHoursStructure
                                toggleDynamicTabs={toggleDynamicTabs}
                                setErrorMessage={setErrorMessage}
                                peninsularAddInfo={peninsularAddInfo}
                                refreshKey={refreshKey}
                                setRefreshKey={setRefreshKey}
                                RunningHrsMachineries={RunningHrsMachineries}
                                editValues={editValues}
                                setEditValues={setEditValues}
                                editInitialValues={editInitialValues}
                                editSubmit={editSubmit}
                                editIsSubmitting={editIsSubmitting}
                            />
                        )}
                    </Formik>
                )
            }
        </Card >
    );
};

export default RunningHoursComponent;
